import React, {useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Checkbox from '@material-ui/core/Checkbox';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Button from '@material-ui/core/Button';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Tooltip, {TooltipProps} from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import {MetricInfo, MetricInfoGroup, MetricParams} from '@store/@types/metric';
import WebDataButton from 'components/utils/WebDataButton';
import {List, Map} from 'immutable';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import ListSubheader from '@material-ui/core/ListSubheader';
import {groupLabels} from 'components/dashboard/manager/nine-box/NineBox.const';

/** Default maximum number of metrics to allow in a single export. */
const DEFAULT_MAX_EXPORT_COUNT = 12;

interface LabeledCheckboxProps {
  readonly metricInfo: MetricInfo;
  // Function to determine if the checkbox is checked
  readonly isChecked: (params: MetricParams) => boolean;
  // Whether to disable unchecked boxes, providing a visual cue that the max number of
  // checked boxes has been reached.
  readonly disableUnchecked: boolean;
  readonly onChange: (event: React.ChangeEvent<HTMLInputElement>, mp: MetricParams) => void;
}

const LabeledCheckbox: React.FC<LabeledCheckboxProps> = ({
  metricInfo,
  isChecked,
  disableUnchecked,
  onChange,
}) => {
  const checked = isChecked(metricInfo.value);
  return (
    <FormControlLabel
      control={
        <Checkbox
          onChange={e => onChange(e, metricInfo.value)}
          name={metricInfo.title}
          checked={checked}
          disabled={!checked && disableUnchecked}
        />
      }
      label={metricInfo.title}
    />
  );
};

interface NineBoxDataExportProps {
  /** the maximum number of metrics that can be exported at once
   *  (aka, the maximum number of boxes we allow to be checked).
   *  @link DEFAULT_MAX_EXPORT_COUNT. */
  readonly maxExports?: number;
  // called with the selected metrics when the user clicks the export button.
  readonly performExport: () => void;
  // Metrics rendered on the X and Y axis
  // These are shown as selected, but tracked separately
  readonly axisSelections: List<MetricInfo>;
  // Options available for export
  readonly exportOptions: List<MetricInfo>;
  // Options selected for export
  readonly exportSelections: Map<MetricParams, boolean>;
  // Toggles whether an export is selected
  readonly toggleExport: (m: MetricParams, checked: boolean) => void;
}

/**
 * A menu containing a labeled checkbox per metric. Unchecked checkboxes are disabled once some configurable maximum
 * number of boxes has been checked.
 *
 */
const NineBoxDataExportMenu: React.FC<NineBoxDataExportProps> = ({
  maxExports = DEFAULT_MAX_EXPORT_COUNT,
  performExport,
  axisSelections,
  exportOptions,
  exportSelections,
  toggleExport,
}) => {
  const [tab, setTab] = useState(0);
  // Check export map first, if not defined, check our default axis selections
  // This allows the user to uncheck something from the axis
  const isChecked = (m: MetricParams) =>
    exportSelections.get(m) ?? !!axisSelections.find(s => m.equals(s.value));

  // number of axis selections which aren't already toggled by the user
  const numAxisSelected = axisSelections.filter(s => !exportSelections.has(s.value)).size;
  // Any exports the user has manually checked
  const userChecked = exportSelections.filter(checked => checked).size;
  const totalChecked = numAxisSelected + userChecked;
  // no more boxes can be checked
  const maxChecked = totalChecked >= maxExports;
  // cannot export unless some metrics are selected.
  const noneChecked = totalChecked === 0;

  const onChange = (event: React.ChangeEvent<HTMLInputElement>, params: MetricParams) => {
    if (maxChecked && event.currentTarget.checked) {
      event.preventDefault(); // keep the checkbox from being checked.
    } else {
      toggleExport(params, event.currentTarget.checked);
    }
  };

  const groupedOptions = exportOptions
    .filter(mi => (tab === 0 ? true : isChecked(mi.value)))
    .groupBy(mi => mi.group);
  const css = useStyles();

  return (
    <div style={{margin: '5px', maxWidth: 350, minHeight: 200}}>
      <Tabs value={tab} onChange={(_, val) => setTab(val)} indicatorColor="primary">
        <Tab label="All Metrics" />
        <Tab label={`Selected Metrics (${totalChecked})`} />
      </Tabs>
      {maxChecked ? (
        <div style={{marginTop: 5}}>
          <Typography color="error">
            You may select up to {maxExports} metrics per export. Deselect one or more to choose
            others.
          </Typography>
        </div>
      ) : null}
      <MenuList className={css.tooltipMenu}>
        {Object.entries(groupLabels).map(([group, label]) => {
          const options = groupedOptions.get(group as MetricInfoGroup, List());
          if (options.isEmpty()) return null;

          return (
            <li>
              <ul style={{padding: 0, backgroundColor: '#FFF'}}>
                <ListSubheader>{label}</ListSubheader>
                {options.map(mi => (
                  <MenuItem key={mi.title}>
                    <LabeledCheckbox
                      metricInfo={mi}
                      onChange={onChange}
                      isChecked={isChecked}
                      disableUnchecked={maxChecked}
                    />
                  </MenuItem>
                ))}
              </ul>
            </li>
          );
        })}
      </MenuList>
      <div style={{textAlign: 'center'}}>
        <Button color="primary" variant="contained" onClick={performExport} disabled={noneChecked}>
          Export&nbsp;&nbsp;
          <ExitToAppIcon />
        </Button>
      </div>
    </div>
  );
};

interface NineBoxPopupMenuProps {
  readonly title: TooltipProps['title'];
  readonly open: boolean;
  readonly setOpen: (open: boolean) => void;
}

/**
 * Wraps the menu in a tooltip.
 *
 * @param title the tooltip "title" (i.e., its contents).
 * @param open whether the tooltip is open.
 * @param setOpen callback that sets open state.
 * @constructor
 */
const NineBoxPopupMenu: React.FC<NineBoxPopupMenuProps> = ({title, open, setOpen}) => {
  const css = useStyles();
  return (
    <Tooltip
      arrow
      disableFocusListener
      disableHoverListener
      disableTouchListener
      interactive
      PopperProps={{
        disablePortal: true,
        className: css.tooltipWidth,
      }}
      open={open}
      title={title}>
      {/* Tooltip needs a child that can take a ref. The button cannot. */}
      <div>
        <WebDataButton onClick={() => setOpen(!open)} tooltip="Export" />
      </div>
    </Tooltip>
  );
};

interface NineBoxDataExportButtonProps extends NineBoxDataExportProps {}

/**
 * Button that brings up the export popup menu.
 */
const NineBoxDataExportButton: React.FC<NineBoxDataExportButtonProps> = ({
  maxExports,
  performExport,
  axisSelections,
  exportOptions,
  exportSelections,
  toggleExport,
}) => {
  const [showTooltip, setShowTooltip] = React.useState<boolean>(false);
  const closeTooltip = () => setShowTooltip(false);

  const title = (
    <NineBoxDataExportMenu
      exportOptions={exportOptions}
      exportSelections={exportSelections}
      toggleExport={toggleExport}
      axisSelections={axisSelections}
      maxExports={maxExports}
      performExport={performExport}
    />
  );

  return (
    <ClickAwayListener onClickAway={closeTooltip}>
      <div>
        <NineBoxPopupMenu open={showTooltip} setOpen={setShowTooltip} title={title} />
      </div>
    </ClickAwayListener>
  );
};

const useStyles = makeStyles(theme => ({
  tooltipMenu: {
    maxHeight: '200px',
    overflowY: 'auto',
    marginTop: 0,
    paddingTop: 0,
    marginBottom: '10px',
  },
  // If there's a better way to keep this tooltip wide enough please fix
  tooltipWidth: {
    zIndex: 1500,
    '& > .MuiTooltip-tooltip': {
      maxWidth: 360,
      width: 360,
    },
  },
}));

export default NineBoxDataExportButton;
